import { Budget } from '../types/budget';
import { CategoryItem, ResponsibleItem, DestinationItem } from '../types/budget';

const STORAGE_KEY = 'budget_app_data';

export interface StorageData {
  budgets: Budget[];
  defaultBudgetId: number | null;
}

// Create a custom event for storage data changes
export const STORAGE_DATA_CHANGED_EVENT = 'storage_data_changed';

// Emit an event when storage data changes
const emitStorageDataChangedEvent = () => {
  window.dispatchEvent(new Event(STORAGE_DATA_CHANGED_EVENT));

  // Also update the last update timestamp for sync comparison
  localStorage.setItem('budget_app_data_last_update', new Date().toISOString());
};

const getStorageData = (): StorageData => {
  const data = localStorage.getItem(STORAGE_KEY);
  try {
    if (!data) {
      return { budgets: [], defaultBudgetId: null };
    }

    const parsedData = JSON.parse(data);

    // Ensure we have a valid structure
    if (!parsedData || typeof parsedData !== 'object') {
      console.warn('Invalid data structure in localStorage, returning empty data');
      return { budgets: [], defaultBudgetId: null };
    }

    // Ensure budgets is an array
    if (!Array.isArray(parsedData.budgets)) {
      console.warn('Budgets is not an array in localStorage, initializing empty array');
      parsedData.budgets = [];
    }

    // Process each budget to ensure dates are properly handled
    const processedBudgets = parsedData.budgets.map((budget: Partial<Budget>) => {
      return {
        ...budget,
        date: budget.date ? new Date(budget.date) : new Date(),
        transactions: Array.isArray(budget.transactions) ? budget.transactions : [],
        categories: Array.isArray(budget.categories) ? budget.categories : [],
        responsibles: Array.isArray(budget.responsibles) ? budget.responsibles : [],
        destinations: Array.isArray(budget.destinations) ? budget.destinations : [],
      };
    });

    return {
      budgets: processedBudgets,
      defaultBudgetId: parsedData.defaultBudgetId || null,
    };
  } catch (error) {
    console.error('Error parsing storage data:', error);
    return { budgets: [], defaultBudgetId: null };
  }
};

const setStorageData = (data: StorageData): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

  // Emit the storage data changed event
  emitStorageDataChangedEvent();

  // Trigger a custom event that React Query hooks can listen for
  // This will help React Query know when to refetch data
  try {
    window.dispatchEvent(
      new CustomEvent('budget_app_data_updated', {
        detail: {
          timestamp: new Date(),
          hasData: data.budgets && data.budgets.length > 0,
          defaultBudgetId: data.defaultBudgetId,
        },
      })
    );
  } catch (error) {
    console.error('Error dispatching budget_app_data_updated event:', error);
  }
};

export const createBudget = (
  budget: Omit<Budget, 'id'>,
  defaults?: {
    categories: CategoryItem[];
    responsibles: ResponsibleItem[];
    destinations: DestinationItem[];
  }
): number => {
  const data = getStorageData();
  // Calculer le nouvel ID en fonction des IDs existants
  const newId = data.budgets.length > 0 ? Math.max(...data.budgets.map((b) => b.id || 0)) + 1 : 1;

  // Ensure the budget has categories and responsibles
  const newBudget = {
    ...budget,
    id: newId,
    date: new Date(),
    // If categories are not provided, use localized default categories
    categories: budget.categories.length > 0 ? budget.categories : (defaults?.categories ?? []),
    // If responsibles are not provided, use localized default responsibles
    responsibles:
      budget.responsibles.length > 0 ? budget.responsibles : (defaults?.responsibles ?? []),
    // If destinations are not provided, use localized default destinations
    destinations:
      budget.destinations.length > 0 ? budget.destinations : (defaults?.destinations ?? []),
  };

  data.budgets.push(newBudget);

  // Un nouveau budget devient automatiquement le budget par défaut
  data.defaultBudgetId = newId;

  setStorageData(data);
  return newId;
};

export const getBudget = (id: number): Budget | null => {
  const data = getStorageData();
  return data.budgets.find((b) => b.id === id) || null;
};

export const getAllBudgets = (): Budget[] => {
  const data = getStorageData();
  return data.budgets;
};

export const updateBudget = (budget: Budget): void => {
  const data = getStorageData();
  data.budgets = data.budgets.map((b) => (b.id === budget.id ? budget : b));
  setStorageData(data);
};

export const duplicateBudget = (id: number, newName: string): number => {
  const data = getStorageData();
  const budget = data.budgets.find((b) => b.id === id);

  if (!budget) return 0;

  return createBudget({
    ...budget,
    name: newName,
    date: new Date(),
  });
};

/**
 * Returns the default budget (or the latest budget if no default is set)
 * This is the main function used by the Dashboard to display the current budget
 *
 * The function first tries to find the budget marked as default (using defaultBudgetId)
 * If no default budget is found or if it doesn't exist anymore, it returns the most recent budget
 */
export const getLatestBudget = (): Budget | null => {
  const data = getStorageData();
  if (data.budgets.length === 0) return null;

  // Chercher d'abord le budget par défaut
  if (data.defaultBudgetId) {
    const defaultBudget = data.budgets.find((b) => b.id === data.defaultBudgetId);
    if (defaultBudget) return defaultBudget;
  }

  // Sinon, retourner le plus récent
  return data.budgets.reduce((latest, current) => {
    const latestDate = new Date(latest.date);
    const currentDate = new Date(current.date);
    return currentDate > latestDate ? current : latest;
  });
};

export const deleteBudget = (id: number): void => {
  const data = getStorageData();
  data.budgets = data.budgets.filter((b) => b.id !== id);

  // If we're deleting the default budget
  if (data.defaultBudgetId === id) {
    // If there are no budgets left, set defaultBudgetId to null
    if (data.budgets.length === 0) {
      data.defaultBudgetId = null;
    } else {
      // Otherwise, set the most recent budget as the default
      const latestBudget = data.budgets.reduce((latest, current) => {
        const latestDate = new Date(latest.date);
        const currentDate = new Date(current.date);
        return currentDate > latestDate ? current : latest;
      }, data.budgets[0]);

      data.defaultBudgetId = latestBudget?.id ?? null;
    }
  }

  setStorageData(data);
};

export const setDefaultBudget = (budgetId: number): void => {
  const data = getStorageData();
  // Vérifier que le budget existe
  if (data.budgets.some((b) => b.id === budgetId)) {
    data.defaultBudgetId = budgetId;
    setStorageData(data);
  }
};

/**
 * Returns just the ID of the default budget
 * This is used by getLatestBudget and for UI elements that need to know
 * which budget is the default (like star icons in the budget list)
 */
export const getDefaultBudgetId = (): number | null => {
  const data = getStorageData();
  return data.defaultBudgetId;
};
